import React, { useCallback } from 'react';
import includes from 'lodash/includes';

import {
  UniversalFilterDateModes,
  UniversalFilterDateOptions,
  UniversalFilterDateFilterState,
  UniversalFilterDateOnFilterStateChange
} from '../../UniversalFilterDate.types';
import { UniversalFilterDateIsWithinModeOptions } from './UniversalFilterDateIsWithinMode.types';

import {
  UniversalFilterOptionSelect,
  UniversalFilterOptionSelectOption,
  UniversalFilterOptionSelectOnChange
} from '../../../../helpers/UniversalFilterOptionSelect';

import { UniversalFilterPositiveIntegerInput } from '../../../../helpers/UniversalFilterPositiveIntegerInput';

import { filtersKeys } from '../../../../../../../locales/keys';

const options: UniversalFilterOptionSelectOption<UniversalFilterDateIsWithinModeOptions>[] =
  [
    {
      value: UniversalFilterDateIsWithinModeOptions.THE_PAST_WEEK,
      i18nText: filtersKeys.dateOptions.thePastWeek
    },
    {
      value: UniversalFilterDateIsWithinModeOptions.THE_PAST_MONTH,
      i18nText: filtersKeys.dateOptions.thePastMonth
    },
    {
      value: UniversalFilterDateIsWithinModeOptions.THE_PAST_YEAR,
      i18nText: filtersKeys.dateOptions.thePastYear
    },
    {
      value: UniversalFilterDateIsWithinModeOptions.THE_NEXT_WEEK,
      i18nText: filtersKeys.dateOptions.theNextWeek
    },
    {
      value: UniversalFilterDateIsWithinModeOptions.THE_NEXT_MONTH,
      i18nText: filtersKeys.dateOptions.theNextMonth
    },
    {
      value: UniversalFilterDateIsWithinModeOptions.THE_NEXT_YEAR,
      i18nText: filtersKeys.dateOptions.theNextYear
    },
    {
      value: UniversalFilterDateIsWithinModeOptions.THIS_CALENDAR_WEEK,
      i18nText: filtersKeys.dateOptions.thisCalendarWeek
    },
    {
      value: UniversalFilterDateIsWithinModeOptions.THIS_CALENDAR_MONTH,
      i18nText: filtersKeys.dateOptions.thisCalendarMonth
    },
    {
      value: UniversalFilterDateIsWithinModeOptions.THIS_CALENDAR_YEAR,
      i18nText: filtersKeys.dateOptions.thisCalendarYear
    },
    {
      value: UniversalFilterDateIsWithinModeOptions.THE_NEXT_NUMBER_OF_DAYS,
      i18nText: filtersKeys.dateOptions.theNextNumberOfDays
    },
    {
      value: UniversalFilterDateIsWithinModeOptions.THE_PAST_NUMBER_OF_DAYS,
      i18nText: filtersKeys.dateOptions.thePastNumberOfDays
    }
  ];

const currentMode = UniversalFilterDateModes.IS_WITHIN;

interface UniversalFilterDateIsWithinModeProps {
  filterState: UniversalFilterDateFilterState;
  autoFocus?: boolean;
  onFilterStateChange: UniversalFilterDateOnFilterStateChange;
}

function UniversalFilterDateIsWithinMode({
  filterState,
  autoFocus,
  onFilterStateChange
}: UniversalFilterDateIsWithinModeProps) {
  const handleChange = useCallback<
    UniversalFilterOptionSelectOnChange<UniversalFilterDateIsWithinModeOptions>
  >(
    (value) => {
      onFilterStateChange({ mode: currentMode, option: value });
    },
    [onFilterStateChange]
  );

  const handleDaysChange = useCallback<(value?: number) => void>(
    (value) => {
      onFilterStateChange({ ...filterState, optionDays: value });
    },
    [filterState, onFilterStateChange]
  );

  const withDaysInput = includes(
    [
      UniversalFilterDateIsWithinModeOptions.THE_NEXT_NUMBER_OF_DAYS,
      UniversalFilterDateIsWithinModeOptions.THE_PAST_NUMBER_OF_DAYS
    ],
    filterState.option
  );

  return (
    <>
      <div className="flex-1">
        <UniversalFilterOptionSelect<UniversalFilterDateOptions>
          options={options}
          selectedValue={filterState.option}
          onChange={handleChange}
        />
      </div>

      {withDaysInput ? (
        <UniversalFilterPositiveIntegerInput
          key={[filterState.mode, filterState.option].join('-')}
          value={filterState.optionDays}
          i18nPlaceholder={filtersKeys.enterDays}
          autoFocus={autoFocus}
          onChange={handleDaysChange}
        />
      ) : null}
    </>
  );
}

export default UniversalFilterDateIsWithinMode;
